package com.hyperether.goodjob.scenes.jobs

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.ui.graphics.painter.BitmapPainter
import androidx.compose.ui.graphics.painter.Painter
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.map.Marker
import com.hyperether.goodjob.models.CompletionReportStatus
import com.hyperether.goodjob.models.Contact
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.JobStatus
import com.hyperether.goodjob.models.Note
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.models.Step
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.repository.Mapper
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.remote.model.CompletionReportRequest
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import com.hyperether.goodjob.repository.remote.model.FileObject
import com.hyperether.goodjob.repository.remote.model.FinishJobRequest
import com.hyperether.goodjob.repository.remote.model.JobRequest
import com.hyperether.goodjob.repository.remote.model.LatLng
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.complete_all_tasks_before_submitting_report
import com.hyperether.goodjob.resources.task_title_error
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.util.Constants
import com.hyperether.goodjob.util.DataUtil
import com.hyperether.goodjob.util.DocumentType
import com.hyperether.goodjob.util.UploadManager
import com.hyperether.goodjob.util.UploadStatus
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.distinctUntilChangedBy
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import org.lighthousegames.logging.logging

class JobDetailsViewModel(
    val repository: Repository,
) : ViewModel() {
    val log = logging("JobDetailsViewModel")

    val showNotePopUp: MutableState<Boolean> = mutableStateOf(false)
    val isInEditMode: MutableState<Boolean> = mutableStateOf(false)

    val showNoteEditDeletePopUpMenu: MutableState<Boolean> = mutableStateOf(false)
    val noteToEditOrDelete: MutableState<Note?> = mutableStateOf(null)
    var selectedNoteId = mutableStateOf<String?>(null)
    var isEditSelectedNote: MutableState<Boolean> = mutableStateOf(false)
    val showAreYouSurePopUpDeleteNote: MutableState<Boolean> = mutableStateOf(false)

    val showJobFinished: MutableState<Boolean> = mutableStateOf(false)
    val showNoteAdded: MutableState<Boolean> = mutableStateOf(false)
    val showNoteDeleted: MutableState<Boolean> = mutableStateOf(false)
    val showPopUpError: MutableState<Boolean> = mutableStateOf(false)
    val showFileAddedSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showFileDeleteSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showFinishedJobSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showRejectedJobSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showReportSubmitSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showErrorCompleteTasks: MutableState<Boolean> = mutableStateOf(false)
    val showLoader: MutableState<Boolean> = mutableStateOf(false)
    val errorText: MutableState<String> = mutableStateOf("")
    val noteInput: MutableState<String> = mutableStateOf("")

    private val _jobDetails = MutableStateFlow<Job?>(null)
    val jobDetails: StateFlow<Job?> = _jobDetails

    private val _contact = MutableStateFlow<Contact?>(null)
    val contact: StateFlow<Contact?> = _contact

    private val _users = MutableStateFlow<List<User>>(emptyList())
    val users: StateFlow<List<User>> = _users

    private val _jobFilesImagePainters =
        MutableStateFlow<List<Pair<FileObject, BitmapPainter>>>(emptyList())
    val jobFilesImagePainters: StateFlow<List<Pair<FileObject, BitmapPainter>>> =
        _jobFilesImagePainters

    private val _jobFilesPdfs =
        MutableStateFlow<List<Pair<FileObject, String>>>(emptyList())
    val jobFilesPdfs: StateFlow<List<Pair<FileObject, String>>> =
        _jobFilesPdfs

    private val _completionReportImagePainters =
        MutableStateFlow<List<Pair<String, BitmapPainter>>>(emptyList())
    val completionReportImagePainters: StateFlow<List<Pair<String, BitmapPainter>>> =
        _completionReportImagePainters

    private val _completionReportPdfs =
        MutableStateFlow<List<Pair<String, String>>>(emptyList())
    val completionReportPdfs: StateFlow<List<Pair<String, String>>> =
        _completionReportPdfs

    private val _clientSignaturePainters =
        MutableStateFlow<List<Pair<String, BitmapPainter>>>(emptyList())
    val clientSignaturePainters: StateFlow<List<Pair<String, BitmapPainter>>> =
        _clientSignaturePainters

    private val _clientSignaturePdfs =
        MutableStateFlow<List<Pair<String, String>>>(emptyList())
    val clientSignaturePdfs: StateFlow<List<Pair<String, String>>> =
        _clientSignaturePdfs

    val clientSignatureFileId = MutableStateFlow<String?>(null)

    private val _fileIds = MutableStateFlow<List<String>>(emptyList())
    val fileIds: StateFlow<List<String>> = _fileIds

    // Completion report
    val damageState = mutableStateOf(false)
    val damageTextState = mutableStateOf("")
    val correctFunctionalityState = mutableStateOf(true)
    val correctFunctionalityTextState = mutableStateOf("")
    val healthAndSafetyState = mutableStateOf(true)
    val healthAndSafetyTextState = mutableStateOf("")
    val additionalNotesText = mutableStateOf("")
    val clientFeedbackText = mutableStateOf("")
    val filesList = mutableStateListOf<String>()

    val showAreYouSurePopUpDeleteFile: MutableState<Boolean> = mutableStateOf(false)
    val selectedFileId = mutableStateOf<String?>(null)

    val showAreYouSurePopUpDeleteImage: MutableState<Boolean> = mutableStateOf(false)
    val selectedImageFileId = mutableStateOf<String?>(null)

    val selectedJob = mutableStateOf<Job?>(null)
    val site = mutableStateOf<Site?>(null)
    var jobStatusMap: Map<String, String> = mutableMapOf()
    val filesOpen: MutableState<Boolean> = mutableStateOf(false)

    val showTaskErrorPopup: MutableState<Boolean> = mutableStateOf(false)

    val showPreviewImage = mutableStateOf(false)
    val previewImageFile = mutableStateOf<Painter?>(null)

    private var filesJob: kotlinx.coroutines.Job? = null
    private var jobListJob: kotlinx.coroutines.Job? = null

    init {
        init()
    }

    fun init() {
        jobDetails
            .filterNotNull()
            .map { it.id }
            .filterNotNull()
            .distinctUntilChangedBy { it }
            .onEach { jobId -> collectFilesById(jobId) }
            .launchIn(viewModelScope)

        viewModelScope.launch {
            repository.getUsersFlow().collect {
                _users.value = it
            }
        }
    }

    private fun collectFilesById(jobId: String) {
        jobListJob?.cancel()
        jobListJob = viewModelScope.launch {
            UploadManager.jobs.collect { jobList ->
                val job = jobList.find { it.id == jobId }
                job?.let {
                    // This line breaks note update, not sure why
//                    _jobDetails.value = job
                    // so update only fileObject
                    _jobDetails.value?.fileObjects = job.fileObjects
//                    log.d("JobDetailsViewModel") { "JobDetailsViewModel collectFilesById notes:${_jobDetails.value?.noteObjects?.size}" }
                }
            }
        }

        filesJob?.cancel()
        filesJob = viewModelScope.launch {
            UploadManager.files.collect { fileList ->
                log.d("FILES") { "collectFilesById fileList:${fileList.size}" }
                val job = jobDetails.value
                val completionReport =
                    Mapper().jsonAsObject<CompletionReportResponse>(job?.completionReport)
                val jobFileIds =
                    job?.fileObjects?.mapNotNull { it?.documentFileId }?.toSet() ?: emptySet()
                val reportFileIds = completionReport?.files?.toSet() ?: emptySet()
                val clientSignatureId =
                    completionReport?.clientSignature?.takeIf { it.isNotBlank() }
//                val allValidIds = buildSet {
//                    addAll(jobFileIds)
//                    addAll(reportFileIds)
//                    clientSignatureId?.let { add(it) }
//                }
                val allValidIds = buildSet {
                    addAll(jobFileIds)
                }

                log.d("FILES") { "All fileList ids: ${fileList.map { it.first }}" }
                val files = DataUtil.enrichFilesWithUsers(fileList, _users.value)

                val filteredFileList = fileList.filter { (fileId, _) ->
                    fileId in allValidIds
                }
                log.d("FILES") { "collectFilesById filteredFileList:${filteredFileList.size} allValidIds: $allValidIds" }

                _clientSignaturePainters.value = files.filter {
                    it.second.jobId == jobId && it.second.painter != null && (it.second.documentType == DocumentType.REPORT_SIGNATURE ||
                            it.second.documentType == DocumentType.REPORT_SIGNATURE_IMAGE)
                }.map { it.first to it.second.painter as BitmapPainter }

                _clientSignaturePdfs.value = files.filter {
                    it.second.jobId == jobId && it.second.path != null && (it.second.documentType == DocumentType.REPORT_SIGNATURE ||
                            it.second.documentType == DocumentType.REPORT_SIGNATURE_PDF)
                }.map { it.first to it.second.path!! }

                _completionReportPdfs.value = files.filter {
                    it.second.jobId == jobId && it.second.path != null && (it.second.documentType == DocumentType.REPORT_FILE_PDF
                            || it.second.documentType == DocumentType.REPORT_FILE)
                }.map { it.first to it.second.path!! }

                _completionReportImagePainters.value = files.filter {
                    it.second.jobId == jobId && it.second.painter != null && (it.second.documentType == DocumentType.REPORT_FILE_IMAGE
                            || it.second.documentType == DocumentType.REPORT_FILE)
                }.map { it.first to it.second.painter as BitmapPainter }

                _jobFilesImagePainters.value = filteredFileList.filter {
                    it.second.jobId == jobId && it.second.painter != null && it.second.documentType == DocumentType.JOB_IMAGE && it.second.fileObject != null
                }.map { it.second.fileObject!! to it.second.painter as BitmapPainter }

                _jobFilesPdfs.value = filteredFileList.filter {
                    it.second.jobId == jobId && it.second.path != null && it.second.documentType == DocumentType.JOB_PDF && it.second.fileObject != null
                }.map { it.second.fileObject!! to it.second.path!! }

                log.d("FILES") { "collectFilesById _jobFilesPdfs: ${_jobFilesPdfs.value.size} _jobFilesImagePainters: ${_jobFilesImagePainters.value.size}" }

                _fileIds.value = files.filter {
                    it.second.jobId == jobId && it.first.isNotEmpty() && (it.second.documentType == DocumentType.REPORT_FILE_IMAGE
                            || it.second.documentType == DocumentType.REPORT_FILE_PDF
                            || it.second.documentType == DocumentType.REPORT_FILE)
                }.map { it.first }
            }
        }
    }

    fun loadJobDetails(jobId: String, onJobDetailsLoaded: () -> Unit = {}) {
        viewModelScope.launch {
            log.d("JobDetailsViewModel") { "JobDetailsViewModel loadJobDetails jobId: $jobId" }
            // TODO: cancel previous collection?
            repository.getJobByIdFlow(jobId).collect { job ->
                job?.let {
                    val eJob = DataUtil.enrichJobAssigneesWithUsers(job, _users.value)
                    val sJob = DataUtil.enrichJobNotesWithUsers(eJob, _users.value)
                    _jobDetails.value = sJob
                    log.d("JobDetailsViewModel") { "JobDetailsViewModel :${sJob}" }
                    showLoader.value = false
                    onJobDetailsLoaded()
                    eJob.contactParentId?.let { contactId ->
                        val result = repository.getContact(contactId)
                        _contact.value = result
                    }
                    eJob.contactSiteId?.let {
                        site.value = repository.getSiteById(it)
                    }
                }
            }
        }
        loadRemoteData()
    }

    fun loadRemoteData() {
        viewModelScope.launch {
            if (isEmployee()) {
                repository.getAllJobsForUser()
            } else {
                repository.getAllJobsForWorkspace()
            }
        }
    }

    fun submitCompletionReport(id: String, onSuccess: () -> Unit, onError: () -> Unit) {
        viewModelScope.launch {
            var tasksClosed = true
            val job = _jobDetails.value
            if (job?.steps?.isNotEmpty() == true) {
                val incompleteSteps =
                    job.steps?.filter { it.stepStatus != Constants.STEP_STATUS_COMPLETED }
                if (incompleteSteps?.isNotEmpty() == true) {
                    onError()
                    errorText.value =
                        stringResourcePlain(Res.string.complete_all_tasks_before_submitting_report)
                    showErrorCompleteTasks.value = true
                    tasksClosed = false
                }
            }

            if (tasksClosed) {
                val result = repository.submitCompletionReport(
                    CompletionReportRequest(
                        damageState.value,
                        correctFunctionalityState.value,
                        additionalNotesText.value,
                        healthAndSafetyState.value,
                        getSignature(),
                        clientFeedbackText.value,
                        files = getReportFileIds(),
                        id,
                        repository.getWorkspaceId(),
                        damageTextState.value,
                        correctFunctionalityTextState.value,
                        healthAndSafetyTextState.value
                    )
                )

                when (result) {
                    is Resource.Success -> {
                        onSuccess()
                    }

                    is Resource.Error -> {
                        errorText.value =
                            result.text ?: stringResourcePlain(Res.string.unknown_error)
                        onError()
                    }
                }
            }
        }
    }

    fun finishJob(id: String, status: String) {
        showLoader.value = true
        viewModelScope.launch {
            when (val result = repository.finishJob(FinishJobRequest(id, status))) {
                is Resource.Success -> {
                    showLoader.value = false
                    when (status) {
                        CompletionReportStatus.completed.name.lowercase() -> {
                            repository.updateJobStatus(id, JobStatus.completed)
                            showFinishedJobSuccess.value = true
                        }

                        CompletionReportStatus.rejected.name.lowercase() -> {
                            repository.updateJobStatus(id, JobStatus.in_progress)
                            showRejectedJobSuccess.value = true
                        }
                    }
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    showLoader.value = false
                    showPopUpError.value = true
                }
            }
        }
    }

    fun updateJob(id: String, job: JobRequest, onSuccess: () -> Unit, onError: () -> Unit) {
        viewModelScope.launch {
            when (val result = repository.updateJob(id, job)) {
                is Resource.Success -> {
                    val updatedJob = result.data
                    _jobDetails.value = updatedJob
                    log.d("JobDetailsViewModel") { "JobDetailsViewModel updateJob notes:${_jobDetails.value?.noteObjects?.size}" }
                    UploadManager.downloadJobFiles(updatedJob, repository, showLoader = null)
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    onError()
                }
            }
        }
    }

    fun addStepToJob(step: Step) {
        val title = step.stepTitle?.trim()
        val j = jobDetails.value
        if (!title.isNullOrBlank() && j != null) {
            val newStep = step.copy(stepTitle = title)
            updateJob(
                job = j.copy(steps = j.steps.orEmpty() + newStep),
                onSuccess = {},
                onError = {
                    showTaskErrorPopup.value = true
                }
            )
        } else {
            errorText.value = stringResourcePlain(Res.string.task_title_error)
            showTaskErrorPopup.value = true
        }
    }

    private fun updateJob(job: Job, onSuccess: () -> Unit, onError: () -> Unit) {
        viewModelScope.launch {
            when (val result = repository.updateJob(job.id!!, Mapper().toJobUpdateRequest(job))) {
                is Resource.Success -> {
                    val updatedJob = result.data
                    _jobDetails.value = updatedJob
                    log.d("JobDetailsViewModel") { "JobDetailsViewModel updateJob2 notes:${_jobDetails.value?.noteObjects?.size}" }
                    UploadManager.downloadJobFiles(updatedJob, repository, showLoader = null)
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    onError()
                }
            }
        }
    }

    fun updateJobStepsStatus(
        id: String,
        steps: List<Step>,
        onSuccess: () -> Unit,
        onError: () -> Unit
    ) {
        viewModelScope.launch {
            when (val result = repository.updateJobStepsStatus(id, steps)) {
                is Resource.Success -> {
                    val updatedJob = result.data
                    _jobDetails.value = updatedJob
                    log.d("JobDetailsViewModel") { "JobDetailsViewModel updateJobStepsStatus notes:${_jobDetails.value?.noteObjects?.size}" }
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    onError()
                }
            }
        }
    }

    fun deleteFileFromJob(jobId: String, fileId: String) {
        showLoader.value = true
        viewModelScope.launch {
            when (val result = repository.deleteFileFromJob(jobId, fileId)) {
                is Resource.Success -> {
                    showLoader.value = false
                    UploadManager.removeFile(fileId)
                    showFileDeleteSuccess.value = true
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    showLoader.value = false
                    showPopUpError.value = true
                    log.d("Delete File Error") { "Error deleting file: ${result.text}" }
                }
            }
        }
    }

    fun deleteImage(jobId: String, fileId: String) {
        showLoader.value = true
        viewModelScope.launch {
            when (val result = repository.deleteFileFromJob(jobId, fileId)) {
                is Resource.Success -> {
                    showLoader.value = false
                    UploadManager.removeFile(fileId)
                    showFileDeleteSuccess.value = true
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    showLoader.value = false
                    showPopUpError.value = true
                    log.d("Delete File Error") { "Error deleting image: ${result.text}" }
                }
            }
        }
    }

    fun deleteFile(fileId: String) {
        viewModelScope.launch {
            showLoader.value = true
            when (val result = repository.deleteFile(fileId)) {
                is Resource.Success -> {
                    UploadManager.removeFile(fileId)
                    filesList.remove(fileId)
                    showLoader.value = false
                    showFileDeleteSuccess.value = true
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    showLoader.value = false
                    showPopUpError.value = true
                }
            }
        }
    }

    fun downloadCompletionReportFiles(completionReportResponse: CompletionReportResponse?) {
        jobDetails.value?.id?.let {
            UploadManager.downloadCompletionReportFiles(
                completionReportResponse,
                it,
                repository,
                showLoader
            )
        }
    }

    fun getUser(): User? {
        return repository.getUser()
    }

    fun isEmployee(): Boolean {
        return repository.getUser()?.role.equals(Role.employee.name, ignoreCase = true)
    }

    fun isAdmin(): Boolean {
        return repository.getUser()?.role.equals(Role.admin.name, ignoreCase = true) ||
                repository.getUser()?.role.equals(Role.superAdmin.name, ignoreCase = true)
    }

    fun isUserAssignedToJob(job: Job?): Boolean {
        if (job == null)
            return false
        val user = getUser()
        val userId = user?._id ?: return false
        val isEmployee = job.assignees?.employees?.any { it?._id == userId } == true

        // Check if any of the user's team IDs match the job's team IDs
        val userTeamIds = user.teams?.mapNotNull { it?.id }?.toSet() ?: emptySet()
        val jobTeamIds = job.assignees?.teams?.mapNotNull { it?._id }?.toSet() ?: emptySet()
        val isInTeam = userTeamIds.intersect(jobTeamIds).isNotEmpty()

        return isEmployee || isInTeam
    }

    private fun getCoordinates(): Pair<Double, Double>? {
        val lat = site.value?.addressObject?.lat
        val long = site.value?.addressObject?.long

        return if (lat != null && long != null) lat to long else null
    }

    fun getSiteMarkers(): List<Marker> {
        return getCoordinates()?.let { (lat, long) ->
            listOf(
                Marker(
                    lat = lat,
                    lng = long,
                    title = site.value?.siteName.orEmpty(),
                    status = jobDetails.value?.status.orEmpty()
                )
            )
        } ?: emptyList()
    }

    fun getSiteCurrentPosition(): LatLng? {
        return getCoordinates()?.let { (lat, long) ->
            LatLng(lat, long)
        }
    }

    fun isFileUploading(): Boolean {
        return jobDetails.value?.let { it.id?.let { jobId -> UploadManager.isFileUploading(jobId) } }
            ?: false
    }

    fun isFileUploadError(): Boolean {
        return jobDetails.value?.let { it.id?.let { jobId -> UploadManager.isFileUploadError(jobId) } }
            ?: false
    }

    fun getSignatureUploadStatus(): UploadStatus {
        return jobDetails.value?.let { UploadManager.getSignatureUploadStatus(it) }
            ?: UploadStatus.NONE
    }

    fun getJobFileIds(): List<String> {
        return jobDetails.value?.let { UploadManager.getFileIds(it) } ?: emptyList()
    }

    fun getReportFileIds(): List<String> {
        return jobDetails.value?.let { UploadManager.getReportFileIds(it) } ?: emptyList()
    }

    fun getSignature(): String? {
        return jobDetails.value?.let { UploadManager.getSignature(it) }
    }

    fun onOpenImageClick(painter: Painter) {
        previewImageFile.value = painter
        showPreviewImage.value = true
    }

    fun clearData() {
        clientSignatureFileId.value?.let {
            UploadManager.removeFile(it)
        }
        UploadManager.removeFiles(fileIds.value)
    }
}